/*----------------------------------
- COMPONENT: PUBLIC DOMAINS ROW CARD
----------------------------------*/

/**
 * @docs docs/features/website-domains-hubs
 * @adr ADR-0028
 *
 * One preview row, laid out for a phone.
 *
 * The same row the table draws, in the only shape a 402px screen can read: identity, status and
 * price on the first line, the read and its bands on a strip that scrolls, a signal line when the
 * row carries one. Every value comes from `resolveTableCellValue` and every mark from
 * `PublicDomainsTableCell`, so the two presentations cannot disagree about a figure, and the
 * bands are derived from the one column catalog rather than a list of their own: a column added
 * to the table appears here on the same deploy.
 *
 * Nothing is computed here. Tapping the card opens authentication naming this domain, exactly
 * as the table's name button does.
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import Icon from '@/client/components/ui/icon/Icon';
import { resolveTableCellValue } from '@uniquedomains/components/domains/table/cellValues';
import {
    SIGNAL_DISPLAY,
    BADGE_TONE_CLASS_BY_TONE,
} from '@uniquedomains/components/contracts/signals/display';
import { pickCardVisual } from '@/client/catalogs/landing/domains/cardVisuals';

import PublicDomainsTableCell from '../table/PublicDomainsTableCell';
import { CARD_ICONS, FILLED_ICON_INDEXES, splitDomainLabel } from '../table/publicTableIdentity';

// Types
import type { PublicDomainsTableColumn } from '@/client/catalogs/landing/domains/publicTableColumns';
import type { TableCellRow } from '@uniquedomains/components/domains/table/cellValues';
import type { DomainsSearchTableRow } from '@uniquedomains/components/contracts/domains/search';

/*----------------------------------
- TYPES
----------------------------------*/

type PublicDomainsRowCardProps = {
    row: TableCellRow;
    projection: DomainsSearchTableRow | null;
    /** The columns the card shows as bands: every catalog column the head does not already own. */
    bandColumns: readonly PublicDomainsTableColumn[];
    /** Receives the row's domain, as the table's name button does. */
    onOpen: (domain: string) => void;
};

/*----------------------------------
- COMPONENT
----------------------------------*/

const PublicDomainsRowCard = ({ row, projection, bandColumns, onOpen }: PublicDomainsRowCardProps) => {
    const visual = pickCardVisual(row.domain);
    const rowIcon = CARD_ICONS[visual.iconIndex];
    const usesFill = FILLED_ICON_INDEXES.includes(visual.iconIndex);
    const label = splitDomainLabel(row.domain);
    const status = resolveTableCellValue('acquisition', row, projection);
    const price = resolveTableCellValue('askPrice', row, projection);
    const renewal = resolveTableCellValue('renewal', row, projection);
    const verdict = resolveTableCellValue('verdict', row, projection);
    const [leadSignal, ...otherSignals] = row.signals ?? [];
    const leadSignalDisplay = leadSignal ? SIGNAL_DISPLAY[leadSignal.type] : null;

    return (
        <button
            type="button"
            class="ud-public-row-card"
            data-testid="domains-row-card"
            aria-label={`Open ${row.domain}`}
            // Wrapped: handed straight to onClick, the handler would receive the click event
            // where it expects the name.
            onClick={() => onOpen(row.domain)}
        >
            <span class="flex items-center gap-3">
                <span
                    class="inline-flex h-10 w-10 flex-none items-center justify-center rounded-[13px]"
                    // The table's own 12% wash, so a row keeps one identity across the two layouts.
                    style={{ background: `color-mix(in srgb, ${visual.color} 12%, #fff)` }}
                >
                    <Icon
                        name={rowIcon}
                        size={18}
                        color={visual.color}
                        fill={usesFill ? visual.color : 'none'}
                        strokeWidth={usesFill ? 0 : 2.1}
                    />
                </span>

                <span class="flex min-w-0 flex-1 flex-col items-start gap-1">
                    <span class="ud-public-row-card__name">
                        {label.name}
                        {label.tld ? <span class="ud-public-table__tld">{label.tld}</span> : null}
                    </span>
                    <PublicDomainsTableCell value={status} align="left" hover={false} />
                </span>

                <span class="flex flex-none flex-col items-end gap-1 whitespace-nowrap">
                    <span class="ud-public-row-card__price">
                        <PublicDomainsTableCell value={price} align="right" hover={false} />
                    </span>
                    {renewal.kind === 'empty' ? null : (
                        <span class="ud-public-row-card__renewal">
                            Renews
                            <PublicDomainsTableCell value={renewal} align="right" hover={false} />
                        </span>
                    )}
                </span>
            </span>

            <span class="ud-public-row-card__bands">
                {verdict.kind === 'empty' ? null : (
                    <span class="ud-public-row-card__band is-verdict">
                        Our read
                        <PublicDomainsTableCell value={verdict} align="left" hover={false} />
                    </span>
                )}
                {bandColumns.map((column) => {
                    const value = resolveTableCellValue(column.id, row, projection);
                    /* A strip of dashes reads as broken data on a phone, where the table's own
                       column header is not there to explain the gap. The table keeps showing it. */
                    if (value.kind === 'empty') return null;

                    return (
                        <span key={column.id} class="ud-public-row-card__band">
                            {column.label}
                            <PublicDomainsTableCell value={value} align="left" hover={false} />
                        </span>
                    );
                })}
            </span>

            {leadSignal && leadSignalDisplay ? (
                <span class={`ud-public-row-card__signal ${BADGE_TONE_CLASS_BY_TONE[leadSignalDisplay.tone]}`}>
                    <Icon name={leadSignalDisplay.icon} size={13} strokeWidth={2} />
                    {leadSignal.label}
                    {otherSignals.length > 0 ? <span class="opacity-70">+{otherSignals.length}</span> : null}
                </span>
            ) : null}
        </button>
    );
};

export default PublicDomainsRowCard;
